import { Link, useForm } from "@inertiajs/react"

import type { SharedProps } from "@/types"
import { AuthCard } from "@/components/auth-card"
import { AuthShell } from "@/components/auth-shell"
import { AuthStatus } from "@/components/auth-status"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

type PasswordChangePageProps = SharedProps & {
  errorSummary: string[]
  paths: {
    root: string
    updatePassword: string
  }
}

export default function PasswordChangePage({
  errorSummary,
  flash,
  paths,
}: PasswordChangePageProps) {
  const form = useForm({
    password: "",
    password_challenge: "",
    password_confirmation: "",
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.patch(paths.updatePassword, {
      onFinish: () =>
        form.reset("password", "password_challenge", "password_confirmation"),
    })
  }

  return (
    <AuthShell
      heroDescription="Changing your password keeps your account protected. We'll ask for your current one first."
      heroPoints={[
        "Your current password is required to confirm the change.",
        "New password must be at least 12 characters.",
        "You'll stay signed in after the update.",
      ]}
      heroTitle="We'll help you update your password."
      pageTitle="Change your password"
    >
      <div className="flex flex-col gap-4">
        <AuthStatus errorSummary={errorSummary} flash={flash} />
        <AuthCard
          description="Update your password and keep your account protected."
          eyebrow="Security"
          title="Change password"
        >
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.password_challenge}>
                <FieldLabel htmlFor="password_challenge">
                  Current password
                </FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password_challenge}
                  autoComplete="current-password"
                  id="password_challenge"
                  name="password_challenge"
                  onChange={(event) =>
                    form.setData("password_challenge", event.target.value)
                  }
                  required
                  type="password"
                  value={form.data.password_challenge}
                />
                <FieldError>{form.errors.password_challenge}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password}>
                <FieldLabel htmlFor="password">New password</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password}
                  autoComplete="new-password"
                  id="password"
                  name="password"
                  onChange={(event) => form.setData("password", event.target.value)}
                  required
                  type="password"
                  value={form.data.password}
                />
                <FieldDescription>Use at least 12 characters.</FieldDescription>
                <FieldError>{form.errors.password}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password_confirmation}>
                <FieldLabel htmlFor="password_confirmation">
                  Confirm new password
                </FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password_confirmation}
                  autoComplete="new-password"
                  id="password_confirmation"
                  name="password_confirmation"
                  onChange={(event) =>
                    form.setData("password_confirmation", event.target.value)
                  }
                  required
                  type="password"
                  value={form.data.password_confirmation}
                />
                <FieldError>{form.errors.password_confirmation}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? "Saving..." : "Save changes"}
                </Button>
                <FieldDescription className="text-center">
                  <Link href={paths.root}>Back to home</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </AuthCard>
      </div>
    </AuthShell>
  )
}
